<script>
    import _ from 'lodash'
    import moment from 'moment'
    import Mousetrap from 'mousetrap'
    import { apiComputed, appComputed, authComputed } from '@state/helpers'
    import Helper from '../utils/helper'
    import $ from '../plugins/vue-gridform/shared/helper'
    import CONSTANTS from '../constants'
    import Rating from '../plugins/vue-gridform/shared/rating'
    import CoachingPreview from './coaching-preview'
    import Radar from './radar'
    import Knob from './knob'
    import DigitalClock from './digital-clock'

    export default {
        name: 'CoachingRun',
        components: {DigitalClock, Knob, Radar, CoachingPreview},
        props: {
            value: {
                type: Boolean,
                default: false
            },
            coaching: {
                type: Object,
                default: () => null
            },
            mode: {
                type: String,
                default: 'view',
                validator: value => ['view', 'edit'].includes(value)
            },
            action: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                tabs: 0,
                saving: false,
                result: [],
                groups: null,
                groupsSummary: null,
                groupsSummaryTotal: {},
                knobvalue: 0,
                knobtotal: null,
                knobcolor: null,
                knobvaluew: 0,
                knobtotalw: null,
                knobcolorw: null,
                knoblist: false,
                radar: null,
                radarOptions: {
                    scale: {
                        animate: true,
                        ticks: {
                            beginAtZero: true,
                            min: 0,
                            max: 100,
                            stepSize: 10,
                            callback(value) {
                                return `${value}%`
                            }
                        },
                        pointLabels: {
                            callback(value) {
                                return value
                            },
                            fontSize: 12,
                            fontStyle: 'normal'
                        }
                    },
                    tooltips: {
                        callbacks: {
                            label(tooltipItem, data) {
                                const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
                                return ` ${value}%  ${$.percentToGrade(value)}`
                            }
                        }
                    }
                },
                stack: false,
                timerActive: false,
                stopTime: 0,
                duration: 0,
                keyHandler: null,
                mousetrapHandler: null,
                metadata: {},
                processing: false,
                undoDuration: null,
                isChanged: false,
                config: null,
                configId: null,
                gf: null,
                isWeighted: false,
                $tabsContainer: null,
                scrollListener: null,
                scrollPos: [0, 0],
                coachingIsValid: true
            }
        },

        computed: {
            ...authComputed,
            ...appComputed,
            ...apiComputed,
            header() {
                return this.coaching.name
            },
            templateHeader() {
                return this.coaching.staffName
            },
            color() {
                return CONSTANTS.MAIN_BACKGROUND
            },
            ratingOptions() {
                return {large: true, toggleHint: true, menu: this.mode === 'edit'}
            },
            showRadar() {
                return !!this.result.find(fl => fl.fieldlist.rating)
            },
            showKnob() {
                return !!this.result.find(fl => fl.fieldlist.rating)
            }
        },
        watch: {
            // bind or unbind keyhandler depending on visibility
            value() {
                this.value ? this._bindKeys() : this._unbindKeys()
            },
            coaching() {
                this.updateCoaching()
            },
            tabs(tab) {
                const lastPos = this.scrollPos[tab]
                if (lastPos) _.delay(() => {
                    this.$tabsContainer.$el.scrollTop = lastPos
                }, 1000)
                if (tab === 1) this.fixTextareas()
            },
            // set coaching to "changed" if timer is active
            duration() {
                if (this.duration !== this.coaching.duration) this.isChanged = true
            }
        },

        mounted() {
            this.$gridform.ready('coachingRun', gf => {
                this.gf = gf
            })
            this.updateCoaching()
            this._bindKeys()
            // get tabs container element
            // listen to scroll event and store scroll position per tab
            this.$nextTick(() => {
                this.$tabsContainer = _.find(this.$refs.tabs.$children, child => {
                    return child.$el.classList.contains('v-tabs-items')
                })
                this.scrollListener = event => {
                    this.scrollPos[this.tabs] = _.get(event.target, 'scrollTop', 0)
                }
                this.$tabsContainer.$el.addEventListener('scroll', this.scrollListener, {passive: true})
            })
        },
        beforeDestroy() {
            this.$tabsContainer.$el.removeEventListener('scroll', this.scrollListener)
            this._unbindKeys()
        },

        methods: {
            _bindKeys() {
                this.keyHandler = e => {
                    if (e.code === 'Escape' && this.mode === 'view') this.close()
                    else if (e.code === 'Escape' && this.mode === 'edit') {
                        this.confirmCancel()
                    }
                    else if (e.code === 'F6' && this.mode === 'edit') {
                        e.preventDefault()
                        this.timerActive = !this.timerActive
                    }
                }
                document.addEventListener('keydown', this.keyHandler, true)
                Mousetrap.bind('* a', () => {
                    if (this.mode !== 'edit') return // use only in edit mode
                    // get all groups, with rating and without internal
                    const groups = this.gf.fieldlist().filter(group => {
                        return !group.fieldlist.noRating && (group.fieldlist.initials || '').charAt(0) !== '_'
                    })
                    // toggle ratings for all groups
                    groups.forEach(item => {
                        this.gf.rating({[item.fieldlist.isMax ? 'min' : 'max']: true, item})
                    })
                })
                // un-/fold all fieldlists (expand/collapse)
                Mousetrap.bind('* f', () => {
                    this.gf.fieldlist().forEach(fl => {
                        const id = fl.fieldlist.id
                        if (id) this.gf.fieldlist({id, toggle: true})
                    })
                })
            },
            _unbindKeys() {
                document.removeEventListener('keydown', this.keyHandler, true)
                Mousetrap.unbind('* a')
                Mousetrap.unbind('* f')
            },

            onResultChange(fd) {
                if (!this.gf) return
                if (fd) {
                    this.isChanged = true
                    // set time if reminder date is set and no time is set
                    if (fd.reminder) {
                        const gfData = this.gf.data()
                        const fieldReminderTime = _.find(gfData, {name: 'reminderTime'})
                        if (fieldReminderTime && !fieldReminderTime.value) {
                            const reminderTime = '08:00'
                            this.gf.values({reminderTime})
                        }
                    }
                }
                this.result = this.gf.fieldlist().filter(group => (group.fieldlist.initials || '').charAt(0) !== '_')
                this.recalc()
            },
            onFieldListChange() {
                this.result = this.gf.fieldlist().filter(group => {
                    return (group.fieldlist?.initials || '').charAt(0) !== '_'
                })
                this.recalc()
            },
            onFab(item) {
                // detect if fieldlist or field has max score
                let isMax = false
                if (item.fieldlist) isMax = item.fieldlist.rating.score === item.fieldlist.rating.max || item.fieldlist.isMax
                else isMax = item.rating.score === item.rating.max
                this.gf.rating({[isMax ? 'min' : 'max']: true, item})
            },
            onFabAction(event) {
                if (!event) return

                const {action, item} = event
                const isField = !!(item.name && _.has(item, 'rating'))

                // min and max works for field or fieldlist
                if (/^(min|max|up|down)$/.test(action)) this.gf.rating({[action]: true, item})

                // unset rating for field or fieldlist
                if (action === 'exclude') {
                    if (isField) this.gf.update({[item.name]: {rating: {...item.rating, noRating: true}}})
                    else this.gf.fieldlist({id: item.fieldlist.id, noRating: true})
                }
                else if (action === 'include') {
                    if (isField) this.gf.update({[item.name]: {rating: {...item.rating, noRating: false}}})
                    else this.gf.fieldlist({id: item.fieldlist.id, noRating: false})
                }
                else if (action === 'weighting') {
                    if (isField) this.gf.update({[item.name]: {weighting: event.weighting}})
                    else this.gf.fieldlist({id: item.fieldlist.id, weighting: event.weighting})
                }
                this.onResultChange(true)
            },

            // build radar data
            // build groups list
            // calc total knob values
            recalc() {
                const R = Rating.fromFieldlists(this.result)
                const labels = R.labels()
                const total = R.total()
                const totalw = R.totalWeighted()
                const result = R.result()
                const resultw = R.resultWeighted()
                const dataset = R.datasetPercent()

                this.groups = R.groupByIds()
                this.groupsSummary = R.summaryGroups()
                this.groupsSummaryTotal = R.summaryTotal()

                this.isWeighted = R.isWeighted()

                this.knobvalue = result
                this.knobtotal = total
                this.knobcolor = R.color()
                this.knobvaluew = resultw
                this.knobtotalw = totalw
                this.knobcolorw = R.colorWeighted()

                if (!this.stack) {
                    const datasets = []
                    datasets.push(createDataset(dataset, result, total))
                    // datasets.push(createDataset(datasetw, resultw, totalw))
                    this.radar = Object.assign({}, this.radar, {datasets, labels})
                }
                else {
                    const datasets = this.radar.datasets
                    datasets.push(createDataset(dataset, result, total))
                    this.radar = Object.assign({}, this.radar, {datasets, labels})
                }

                function createDataset(data, result, total) {
                    const percent = (result * 100) / total
                    const label = `${result} / ${total} P  ${percent.toFixed(1)}%  ${$.percentToGrade(percent)}`
                    const rgb = $.percentToColor(percent)
                    const backgroundColor = $.percentToColor(percent, 0.4)
                    const pointBackgroundColor = $.percentToColor(percent, 0.8)
                    const datasetOptions = {
                        lineTension: 0.1,
                        backgroundColor,
                        borderColor: rgb,
                        pointBackgroundColor,
                        pointBorderColor: '#fff',
                        pointRadius: 5,
                        pointHoverRadius: 8
                        // pointHoverBackgroundColor: '#fff',
                        // pointHoverBorderColor: 'rgba(102, 153, 204, 1)'
                    }
                    return {label, data, ...datasetOptions}
                }
            },

            bewertung(item) {
                const percent = item.rating.percent
                let _class = 'item-bewertung-yellow'
                if (percent === 0) _class = 'item-bewertung-red'
                if (percent === 100) _class = 'item-bewertung-green'
                return _class
            },
            gewichtungSum(group) {
                const { wscore, wmax } = group.rating
                return wmax ? `${this.$numeral(wscore)} / ${this.$numeral(wmax)} P` : ''
            },
            groupRating(group) {
                return `${this.$numeral(group.wscore)} / ${this.$numeral(group.wmax)} P`
            },
            groupSummaryRating(groupSum) {
                return `${this.$numeral(groupSum.resultWeighted)} / ${this.$numeral(groupSum.totalWeighted)} P`
            },
            grade(group) {
                return $.percentToGrade(group.rating.percent)
            },
            hasNoRating(group) {
                return !!_.find(group.rows, field => !_.get(field, 'rating.max'))
            },
            hasRating(group) {
                return _.get(group, 'rating.max')
            },
            groupName(group) {
                return `${group.initials && group.initials.charAt(0) !== '_' ? group.initials + ': ' : ''}${group.name}`
            },

            setConfig() {
                const config = Helper.coachingToConfig(this.coaching)
                const flattenConfig = _.flatten(config)
                this.metadata.username = initField('username', this.currentUser.user.username)
                this.metadata.employee = initField('employee', this.coaching.staffName)
                this.metadata.staff = initField('staff', this.coaching.staffName)
                this.metadata.coachingDate = this.coaching.coachingDate
                if (!this.metadata.coachingDate) {
                    initField('coachingDate', moment().format('YYYY-MM-DD'))
                    initField('coachingDateTime', moment().format('HH:mm'))
                }
                this.metadata.reminderDate = this.coaching.reminderDate
                this.metadata.finishedDate = this.coaching.finished
                this.configId = this.coaching.id
                this.config = config

                function initField(name, value) {
                    const field = flattenConfig.find(row => {
                        return row.name === name
                    })
                    if (!field) return value
                    if (!field.value) field.value = value
                    return field.value
                }
            },
            updateCoaching() {
                // reset scroll position for new coaching
                this.scrollPos = [0, 0]
                if (this.$tabsContainer) this.$tabsContainer.$el.scrollTop = 0
                if (/^(pdf|print|send)$/.test(this.action)) this.tabs = 1
                this.isChanged = false
                this.timerActive = false
                this.duration = this.coaching.duration
                this.stopTime = this.coaching.duration
                this.setConfig()
                this.$nextTick(() => {
                    this.onResultChange() // trigger recalc, if new values do not fire onFieldChange
                    if (/^(pdf|print|send)$/.test(this.action)) {
                        // NOTE needed, otherwise radar is not visible in pdf
                        _.delay(() => {
                            const params = {auto: false}
                            if (this.action === 'print') params.print = true
                            if (this.action === 'send') params.send = true
                            this.pdfSummary(params).then(() => {this.close()})
                        }, 1000)
                    }
                })
            },
            saveCoaching(finished, saveAndClose = false) {
                // validate gridform if coaching is already finished
                if (this.coaching.isFinished && !this.isValid()) {
                    this.$toast.open({
                        text: this.$vuetify.lang.t('$vuetify.coaching.finishActionError'),
                        type: CONSTANTS.TYPE_ERROR
                    })
                    return
                }

                this.saving = true
                const coachingId = this.coaching.id
                const name = this.coaching.name
                // extract current coaching date
                const gfData = this.gf.data()
                const { coachingDate, reminderDate } = Helper.coachingDates(gfData)
                const coachingData = {config: this.gf.export(), duration: this.duration, coachingDate, reminderDate}
                const payload = {coachingId, name, coachingData}
                if (finished) payload.isFinished = true // do not set finished to false if already true
                this.$store.dispatch('api/COACHING_UPDATE', payload).then(successful => {
                    this.saving = false
                    if (successful) {
                        this.isChanged = false
                        this.$toast.open({
                            text: this.$vuetify.lang.t('$vuetify.coaching.saved'),
                            type: CONSTANTS.TYPE_SUCCESS,
                            timeout: 3000,
                            offsetY: !saveAndClose ? 8 : void 0
                        })
                        if (saveAndClose) this.close()
                    }
                })
            },
            async finishCoaching() {
                if (!this.isValid()) return

                const submitted = await this.$gridform.dialog({
                    message: this.i18n('finishMessage'),
                    text: this.i18n('finishDescription'),
                    submit: this.i18n('finishSubmit')
                })
                if (!submitted) return

                this.saveCoaching(true, true)
            },
            cancelCoaching() {
                this.confirmCancel()
            },
            close() {
                this.timerActive = false
                this.stopTime = this.duration
                this.saving = false
                this.undoDuration = null
                this.$emit('close')
            },
            // popup confirm dialog if user wants to cancel and there are changes in the coaching
            async confirmCancel() {
                if (!this.hasChanges()) return this.close()

                const answer = await this.$gridform.dialog({
                    title: this.i18n('confirmCancelTitle'),
                    text: this.i18n('confirmCancelDescription'),
                    // TODO use actions[] for buttons
                    discard: true
                })
                if (!answer) return // cancel the cancel
                if (answer === true) this.saveCoaching(false, true)
                else if (answer.discard) this.close()
            },
            // is coaching edited?
            hasChanges() {
                const coachingData = $.parseJSON(this.coaching.coachingData)
                coachingData.duration = coachingData.duration || 0
                if (coachingData.duration !== this.duration) return true
                return this.isChanged
            },
            // validate gridform
            isValid() {
                this.gf.validate()
                this.coachingIsValid = this.gf.isValid()
                // TODO show message?
                return this.coachingIsValid
            },

            async domToImageSummary() {
                const filename = this.i18n('imageFilename').replace('{date}', moment().format('YYYYMMDD-HHmm'))
                await this.$domToImage(this.$refs.summary, filename)
            },
            async pdfSummary(params) {
                let selection
                let oldGroups

                // show checklist dialog for radar and group selection
                if (!params || !params.auto) {
                    // get all not internal groups
                    const groups = Helper.coachingToGroups(this.coaching, {internal: false})
                    const hasRating = !!_.find(groups, group => {
                        return !!_.find(group, f => !!f.rating && !f.rating.noRating)
                    })
                    const hasNoRating = !!_.find(groups, group => {
                        return !!_.find(group, f => !f.rating || f.rating.noRating)
                    })
                    const checklist = []

                    // append checklist item for radar chart
                    if (this.$refs.report) checklist.push({name: 'radar', inputValue: true, label: this.i18n('pdfWithRadar'), hideDetails: true})
                    // do not ask for "no rating", if all blocks are with "no rating"
                    if (hasRating && hasNoRating) checklist.push({name: 'noRating', inputValue: true, label: this.i18n('pdfWithNoRating'), hideDetails: true})

                    // append checklist item for every group
                    if (!_.isEmpty(groups)) {
                        if (this.$refs.report || (hasRating && hasNoRating)) checklist.push({divider: true})
                        _.each(groups, group => {
                            const name = _.get(group[0], 'fieldlist.id')
                            const label = _.get(group[0], 'fieldlist.name', '')
                            const description = _.get(group[0], 'fieldlist.description', '')
                            checklist.push({name, inputValue: true, label: `${label}: ${description}`, hideDetails: true})
                        })
                    }

                    selection = await this.$gridform.dialog({
                        title: this.i18n('pdfSelectionTitle'),
                        description: this.i18n('pdfSelectionDescription'),
                        checklist,
                        large: true
                    })
                    if (!selection) return

                    // check if almost one selection is true
                    const hasSelection = _.find(selection, selected => selected)
                    if (!hasSelection) return

                    // when groups defined, filter selected groups to change DOM for pdf creation
                    if (!_.isEmpty(groups)) {
                        oldGroups = this.groups
                        const newGroups = {}
                        _.keys(this.groups).forEach(group => {
                            // clone group, maybe we modify something like filtering rows
                            if (selection[group]) newGroups[group] = _.cloneDeep(this.groups[group])
                            // do not show blocks without rating
                            // filter out all fields with no rating
                            if (!selection.noRating) {
                                newGroups[group].rows = newGroups[group].rows.filter(field => {
                                    return !!field.rating && !field.rating.noRating
                                })
                            }
                        })
                        this.groups = newGroups
                    }
                }

                await this.$nextTick()

                // visualize processing
                this.processing = true
                const text = this.i18n('summaryProcessing')
                const toast = this.$toast.open({text, timeout: -1, offsetY: 8})

                const {logo, footer} = Helper.pdfInit()
                const title = this.i18n('summaryTitle')
                const subtitle = ''
                const print = _.get(params, 'print')
                const send = _.get(params, 'send')
                const nodes = []
                nodes.push(this.$refs.summary.$el.querySelectorAll('.coaching-summary-group'))
                if (!selection || selection.radar) {
                    if (this.$refs.report) nodes.push(this.$refs.report.querySelectorAll('.coaching-summary-group'))
                }
                const mom = this.coaching.isFinished ? moment(this.coaching.finished) : moment()
                const filename = this.i18n('pdfFilename').replace('-{date}', mom.format('YYYYMMDD-HHmm'))
                return this.$domToPDF(nodes, {title, subtitle, footer, logo, print, send, open: false}, filename)
                    .then(result => {
                        if (send && result) {
                            // send request to API endpoint to send e-mail
                            this.$store.dispatch('api/ORG_SEND_PDF', {
                                userId: this.currentUser.user.userId,
                                coachingId: this.coaching.id,
                                base64PdfString: result
                            }).then(successful => {
                                if (successful) {
                                    this.$toast.open({
                                        text: this.i18n('emailSent'),
                                        type: CONSTANTS.TYPE_SUCCESS,
                                        offsetY: 8
                                    })
                                }
                            })
                        }
                    })
                    .catch(Helper.errorHandler)
                    .finally(() => {
                        this.processing = false
                        if (oldGroups) this.groups = oldGroups
                        const $toast = document.getElementById(toast.id)
                        if ($toast && typeof $toast.close === 'function') $toast.close()
                    })
            },
            sendCoaching() {
                const params = {auto: false, send: true}
                this.pdfSummary(params)
            },

            resetTimer() {
                this.undoDuration = this.duration
                this.duration = 0
                this.stopTime = 0
            },
            undoResetTimer() {
                this.duration = this.undoDuration
                this.stopTime = this.undoDuration
                this.undoDuration = null
            },
            formatTimestamp(s) {
                return $.formatTimestamp(s * 1000, false)
            },
            formatUserDate(date) {
                if (!date) return ''
                return moment.utc(date).local().format(this.formatter.datetime)
            },

            // recalculate textarea input heights
            // NOTE has wrong height when tab is not visible
            fixTextareas() {
                this.$nextTick(() => {
                    _.defer(() => {
                        const tas = document.querySelectorAll('.item-group-table .v-textarea--auto-grow')
                        _.each(tas, ta => {
                            const recalc = _.get(ta, '__vue__.calculateInputHeight')
                            if (typeof recalc === 'function') recalc()
                        })
                    })
                })
            },

            i18n(key) {
                return this.$vuetify.lang.t(`$vuetify.coaching.${key}`)
            }
        }
    }
</script>

<template>
    <VCard class="coaching-run flex-row">
        <VToolbar dense :color="color" :light="$vuetify.theme.dark" :dark="!$vuetify.theme.dark" :elevation="4">
            <VToolbarTitle class="hidden-sm-and-down" :title="developerMode ? coaching.id : ''">{{ header }}</VToolbarTitle>
            <div class="coaching-run__subtitle caption mx-md-8 hidden-sm-and-down">{{ templateHeader }}</div>
            <VSpacer/>

            <VDivider v-if="!$vuetify.breakpoint.smAndDown" vertical class="mr-2"/>
            <div class="digital-clock-wrapper">
                <VTooltip v-if="mode === 'edit'" bottom>
                    <template v-slot:activator="{ on }">
                        <VBtn icon tile small v-on="on" @click.left="timerActive = !timerActive">
                            <VIcon small>{{ !timerActive ? 'mdi-play' : 'mdi-pause'}}</VIcon>
                        </VBtn>
                    </template>
                    <span>{{ $vuetify.lang.t(`$vuetify.coaching.${timerActive ? 'stopTimer' : 'startTimer'}`) }} (F6)</span>
                </VTooltip>
                <DigitalClock
                    v-model="timerActive"
                    :timer="stopTime"
                    :dark="!$vuetify.theme.dark"
                    :dense="$vuetify.breakpoint.smAndDown"
                    :disabled="mode === 'view'"
                    @change="duration = $event"
                    @toggle="timerActive = !timerActive"
                />
                <VTooltip v-if="!timerActive && duration && mode === 'edit'" bottom>
                    <template v-slot:activator="{ on }">
                        <VBtn icon tile small class="ml-1" v-on="on" @click.left="resetTimer">
                            <VIcon small>mdi-rewind-outline</VIcon>
                        </VBtn>
                    </template>
                    <span>{{ $vuetify.lang.t('$vuetify.coaching.resetTimer') }}</span>
                </VTooltip>
                <VTooltip v-if="!timerActive && undoDuration && mode === 'edit'" bottom>
                    <template v-slot:activator="{ on }">
                        <VBtn icon tile small class="ml-1" v-on="on" @click.left="undoResetTimer">
                            <VIcon small>mdi-undo</VIcon>
                        </VBtn>
                    </template>
                    <span>{{ $vuetify.lang.t('$vuetify.coaching.undoResetTimer') }} ({{ formatTimestamp(undoDuration) }})</span>
                </VTooltip>
            </div>
            <VDivider v-if="!$vuetify.breakpoint.smAndDown" vertical/>

            <VSpacer/>
            <VTooltip v-if="mode === 'edit'" :disabled="!$vuetify.breakpoint.mdAndDown" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn text outlined :small="$vuetify.breakpoint.smAndDown" class="ml-2" :disabled="!coachingIsValid" :loading="saving" v-on="on" @click.left="finishCoaching">
                        <VIcon>mdi-check-all</VIcon>
                        <span v-if="!$vuetify.breakpoint.mdAndDown" class="ml-1">{{ i18n('finishAction') }}</span>
                    </VBtn>
                </template>
                <span>{{ i18n('finishAction') }}</span>
            </VTooltip>
            <VTooltip v-if="mode === 'edit'" :disabled="!$vuetify.breakpoint.mdAndDown" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn text outlined :small="$vuetify.breakpoint.smAndDown" class="ml-2" :loading="saving" v-on="on" @click.left="saveCoaching(false)">
                        <VBadge :color="isChanged ? 'accent' : 'transparent'" dot>
                            <VIcon>mdi-content-save-outline</VIcon>
                            <span v-if="!$vuetify.breakpoint.mdAndDown" class="ml-1">{{ $vuetify.lang.t('$vuetify.actions.save') }}</span>
                        </VBadge>
                    </VBtn>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.actions.save') }}</span>
            </VTooltip>
            <VTooltip v-if="mode === 'edit'" :disabled="!$vuetify.breakpoint.mdAndDown" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn text outlined :small="$vuetify.breakpoint.smAndDown" class="ml-2" :loading="saving" v-on="on" @click.left="saveCoaching(false, true)">
                        <VBadge :color="isChanged ? 'accent' : 'transparent'" dot>
                            <VIcon>mdi-archive</VIcon>
                            <span v-if="!$vuetify.breakpoint.mdAndDown" class="ml-1">{{$vuetify.lang.t('$vuetify.actions.saveAndClose')}}</span>
                        </VBadge>
                    </VBtn>
                </template>
                <span>{{$vuetify.lang.t('$vuetify.actions.saveAndClose')}}</span>
            </VTooltip>
            <VTooltip v-if="mode === 'edit'" :disabled="!$vuetify.breakpoint.mdAndDown" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn text outlined :small="$vuetify.breakpoint.smAndDown" class="ml-2" v-on="on" @click.left="confirmCancel">
                        <VIcon>mdi-exit-to-app</VIcon>
                        <span v-if="!$vuetify.breakpoint.mdAndDown" class="ml-1">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</span>
                    </VBtn>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</span>
            </VTooltip>
            <VTooltip v-if="mode === 'view'" :disabled="!$vuetify.breakpoint.mdAndDown" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn text outlined :small="$vuetify.breakpoint.smAndDown" class="ml-2" v-on="on" @click.left="close">
                        <VIcon>mdi-exit-to-app</VIcon>
                        <span v-if="!$vuetify.breakpoint.mdAndDown" class="ml-1">{{ $vuetify.lang.t('$vuetify.actions.close') }}</span>
                    </VBtn>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.actions.close') }}</span>
            </VTooltip>
        </VToolbar>

        <VTabs ref="tabs" v-model="tabs" class="coaching-run__tabs" centered>
            <VTab>
                <VIcon left>mdi-format-list-checks</VIcon>
                {{ $vuetify.lang.t('$vuetify.entity.sheet') }}
            </VTab>
            <VTab>
                <VIcon left>mdi-chart-areaspline</VIcon>
                {{ $vuetify.lang.t('$vuetify.entity.report') }}
            </VTab>

            <!-- Coaching Preview -->
            <VTabItem>
                <VLayout class="justify-center">
                    <VFlex md12 lg10 xl8>
                        <CoachingPreview
                            name="coachingRun"
                            class="pt-8"
                            :uuid="configId"
                            :rows="config"
                            :disabled="mode === 'view'"
                            :validate="mode === 'edit'"
                            :rating="ratingOptions"
                            no-actions
                            @change="onResultChange"
                            @fieldlist="onFieldListChange"
                            @fab="onFab"
                            @fabaction="onFabAction"
                        />
                        <VContainer v-if="mode === 'edit' && !coaching.isFinished" class="mb-8">
                            <VFlex row class="justify-center">
                                <VBtn
                                    color="accent"
                                    :disabled="!coachingIsValid"
                                    :class="theme === 'dark' ? 'black--text' : 'white--text'"
                                    @click.left="finishCoaching">
                                    {{ i18n('finishAction') }}
                                </VBtn>
                            </VFlex>
                            <VFlex row class="justify-center">
                                <VAlert v-if="!coachingIsValid" dense outlined type="error" class="mt-6">{{ i18n('finishActionError') }}</VAlert>
                            </VFlex>
                        </VContainer>
                    </VFlex>
                </VLayout>
            </VTabItem>

            <!-- Reporting -->
            <VTabItem>
                <VLayout class="justify-center">
                    <VFlex md12 lg10 xl8>
                        <VRow class="mt-4">
                            <!-- Radar -->
                            <VCol v-if="showRadar" ref="report" cols="12" md="9">
                                <VCard elevation="4" class="coaching-summary-group">
                                    <div class="pa-2">
                                        <VSwitch v-model="stack" label="Stack datasets" class="d-none"/>
                                        <Radar :chart-data="radar" :options="radarOptions"/>
                                    </div>
                                </VCard>
                            </VCol>

                            <!-- Result Knob -->
                            <VCol v-if="showKnob" cols="12" md="3">
                                <VCard elevation="4">
                                    <VToolbar flat dense>
                                        <VToolbarTitle>{{ $vuetify.lang.t('$vuetify.entity.rating') }}</VToolbarTitle>
                                    </VToolbar>
                                    <VList v-if="knoblist" class="scrollable-y h512" dense two-line subheader>
                                        <template v-for="(group, name) in result">
                                            <VSubheader :key="name" v-text="name"/>
                                            <VListItem
                                                v-for="(item, index) in group"
                                                :key="item.initials + index"
                                                dense
                                            >
                                                <template v-slot:default>
                                                    <VListItemAvatar>
                                                        <VIcon :class="['value-badge grey lighten-2']">{{ item.value }}</VIcon>
                                                    </VListItemAvatar>
                                                    <VListItemContent>
                                                        <VListItemTitle v-text="item.label"/>
                                                        <VListItemSubtitle v-text="item.type"/>
                                                    </VListItemContent>
                                                    <VListItemAction>
                                                        <VIcon>mdi-pencil</VIcon>
                                                    </VListItemAction>
                                                </template>
                                            </VListItem>
                                        </template>
                                    </VList>
                                    <Knob v-if="knobtotal" v-model="knobvalue" :total="knobtotal" :color="knobcolor" :dark="$vuetify.theme.dark"/>
                                </VCard>
                                <VCard v-if="isWeighted" class="mt-3" elevation="4">
                                    <VToolbar flat dense>
                                        <VToolbarTitle>{{ $vuetify.lang.t('$vuetify.entity.ratingWeighted') }}</VToolbarTitle>
                                    </VToolbar>
                                    <Knob v-if="knobtotalw" v-model="knobvaluew" :total="knobtotalw" :color="knobcolorw" :dark="$vuetify.theme.dark"/>
                                </VCard>
                            </VCol>

                            <!-- TODO : build component for this summary -->
                            <VCol cols="12">
                                <VCard elevation="4">
                                    <VToolbar flat :dense="dense">
                                        <VToolbarTitle>{{ $vuetify.lang.t('$vuetify.entity.summary') }}</VToolbarTitle>
                                        <VSpacer/>
                                        <VBtn icon small :loading="processing" class="mx-1" @click.left="sendCoaching">
                                            <VIcon>mdi-email-outline</VIcon>
                                        </VBtn>
                                        <VBtn icon small :loading="processing" class="mx-1" @click.left="pdfSummary">
                                            <VIcon>mdi-file-pdf-box</VIcon>
                                        </VBtn>
                                        <VBtn icon small :loading="processing" class="mx-1" @click.left="domToImageSummary">
                                            <VIcon>mdi-camera</VIcon>
                                        </VBtn>
                                    </VToolbar>
                                    <VSheet ref="summary" tile class="pa-4">
                                        <!-- Coaching metadata -->
                                        <div class="coaching-summary-group pb-4">
                                            <div>
                                                <label class="d-inline-block font-weight-bold pr-6" style="min-width:150px">{{ $vuetify.lang.t('$vuetify.entity.user') }}:</label>
                                                <span v-text="metadata.username"/>
                                            </div>
                                            <div>
                                                <label class="d-inline-block font-weight-bold pr-6" style="min-width:150px">{{ $vuetify.lang.t('$vuetify.entity.employee') }}:</label>
                                                <span v-text="metadata.employee"/>
                                            </div>
                                            <div>
                                                <label class="d-inline-block font-weight-bold pr-6" style="min-width:150px">{{ $vuetify.lang.t('$vuetify.entity.template') }}:</label>
                                                <span v-text="coaching.name"/>
                                            </div>
                                            <div>
                                                <label class="d-inline-block font-weight-bold pr-6" style="min-width:150px">{{ $vuetify.lang.t('$vuetify.entity.date') }}:</label>
                                                <span v-text="formatUserDate(metadata.coachingDate)"/>
                                            </div>
                                            <div>
                                                <label class="d-inline-block font-weight-bold pr-6" style="min-width:150px">{{ $vuetify.lang.t('$vuetify.entity.reminder') }}:</label>
                                                <span v-text="formatUserDate(metadata.reminderDate)"/>
                                            </div>
                                            <div class="mb-4">
                                                <label class="d-inline-block font-weight-bold pr-6" style="min-width:150px">{{ $vuetify.lang.t('$vuetify.entity.finished') }}:</label>
                                                <span v-text="formatUserDate(metadata.finishedDate)"/>
                                            </div>

                                            <hr class="mb-4"/>
                                        </div>

                                        <!-- Oneline summary -->
                                        <div v-if="groupsSummaryTotal.total" class="coaching-summary-group mt-1 pb-4">
                                            <v-subheader class="pa-0">{{ $vuetify.lang.t('$vuetify.entity.summaryBuildingBlocks') }}</v-subheader>
                                            <table class="item-group-table-summary">
                                                <thead>
                                                <tr>
                                                    <th class="text-left">Name</th>
                                                    <th class="text-left">{{ $vuetify.lang.t('$vuetify.entity.note') }}</th>
                                                    <th class="text-center">{{ $vuetify.lang.t('$vuetify.entity.result') }}</th>
                                                    <th class="item-gewichtung-diff text-right">{{ $vuetify.lang.t('$vuetify.entity.weighting') }}</th>
                                                    <th class="text-right">{{ $vuetify.lang.t('$vuetify.entity.rating') }}</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <template v-for="group in groupsSummary">
                                                    <tr v-if="group.name" :key="`grp-${group.key}`">
                                                        <td>{{ group.name }}</td>
                                                        <td>{{ group.grade }}</td>
                                                        <td class="text-center">{{ groupRating(group) }}</td>
                                                        <td class="item-gewichtung-diff text-right">{{ group.diff ? $numeral(group.diff) : '' }}</td>
                                                        <td class="text-right">{{ $numeral(group.percent) + '%' }}</td>
                                                    </tr>
                                                </template>
                                                <tr></tr>
                                                <tr class="sum-group-table-footer">
                                                    <td>{{ $vuetify.lang.t('$vuetify.entity.total') }}</td>
                                                    <td>{{ groupsSummaryTotal.grade }}</td>
                                                    <td class="text-center">{{ groupSummaryRating(groupsSummaryTotal) }}</td>
                                                    <td class="item-gewichtung-diff text-right">{{ groupsSummaryTotal.diff ? $numeral(groupsSummaryTotal.diff) : '' }}</td>
                                                    <td class="text-right">{{ $numeral(groupsSummaryTotal.percent) + '%' }}</td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <v-subheader class="pa-0">{{ $vuetify.lang.t('$vuetify.entity.blocks') }}</v-subheader>
                                        <template v-for="(group, name) in groups">
                                            <div :key="group.key" class="coaching-summary-group my-4 pb-4">
                                                <div v-if="name && name !== 'undefined'" class="item-group-name" v-text="groupName(group)"></div>
                                                <table class="item-group-table">
                                                    <thead v-if="name">
                                                    <tr>
                                                        <th/>
                                                        <th class="text-center">{{ $vuetify.lang.t('$vuetify.entity.result') }}</th>
                                                        <th class="item-gewichtung-diff text-right">{{ $vuetify.lang.t('$vuetify.entity.weighting') }}</th>
                                                        <th>{{ $vuetify.lang.t('$vuetify.entity.rating') }}</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    <tr v-for="(item, gindex) in group.rows" :key="group.key + `${gindex}`">
                                                        <td>
                                                            <VSheet style="background: transparent">
                                                                <div class="item-label subtitle-2" v-text="item.label"></div>
                                                                <div v-if="item.hint" class="item-hint caption" v-text="item.hint"></div>
                                                                <VSheet v-if="item.data && item.data.length" style="background: transparent">
                                                                    <template v-for="(data, index) in item.data">
                                                                        <VTextarea v-if="item._type === 'text' || item._type === 'textarea'" :key="index" :value="data" disabled dense outlined flat full-width hide-details auto-grow rows="1" :row-height="24" no-resize background-color="transparent"/>
                                                                        <VRating v-else-if="item._type === 'rating'" :key="index" :value="data" readonly></VRating>
                                                                        <VChip v-else :key="index" outlined small>{{data}}</VChip>
                                                                    </template>
                                                                </VSheet>
                                                            </VSheet>
                                                        </td>
                                                        <td class="item-gewichtung-row text-center">
                                                            <template v-if="item._type !== 'gap'">
                                                                <div v-if="item.rating && item.rating.max && !item.rating.noRating">{{ groupRating(item.rating) }}</div>
                                                                <div v-else>-</div>
                                                            </template>
                                                        </td>
                                                        <td class="item-gewichtung-diff text-right">
                                                            <template v-if="item._type !== 'gap'">
                                                                <div v-if="item.rating && item.rating.score !== item.rating.wscore && !item.rating.noRating">{{$numeral(item.rating.score - item.rating.wscore)}}</div>
                                                            </template>
                                                        </td>
                                                        <td class="item-bewertung text-center">
                                                            <template v-if="item._type !== 'gap'">
                                                                <div v-if="item.rating && item.rating.max && !item.rating.noRating" class="mx-auto" :class="bewertung(item)"></div>
                                                                <div v-else>{{ i18n('noRatingHintShort') }}</div>
                                                            </template>
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                                <div v-if="hasNoRating(group)" class="overline text-right">{{ i18n('noRatingHint') }}</div>
                                                <table v-if="hasRating(group)" class="item-group-table-footer">
                                                    <tbody>
                                                    <tr>
                                                        <td>{{ $vuetify.lang.t('$vuetify.entity.total') }}</td>
                                                        <td class="item-gewichtung-row text-center" v-text="gewichtungSum(group)">P / P</td>
                                                        <td class="item-gewichtung-diff text-end">{{ group.rating.diff ? $numeral(group.rating.diff) : '' }}</td>
                                                        <td class="item-bewertung text-center">{{ group.rating.percent ? $numeral(group.rating.percent) + '%' : '' }}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>{{ $vuetify.lang.t('$vuetify.entity.note') }}</td>
                                                        <td class="text-right" colspan="3" style="padding-right:8px" v-text="grade(group)">diese</td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </template>
                                    </VSheet>
                                </VCard>
                            </VCol>
                        </VRow>
                    </VFlex>
                </VLayout>
            </VTabItem>
        </VTabs>
    </VCard>
</template>

<!-- NOTE do not scope, we need to overwrite .v-tabs-items from vuetify, check why! -->
<style lang="scss">
    .coaching-run {
        position: absolute;
        max-height: 100%;
        overflow: hidden;
    }
    .coaching-run__subtitle {
        max-width: 50%;
    }
    .coaching-run .v-tabs-items {
        height: calc(100% - 48px);
        overflow: auto;
        .v-window__container {
            height: 100%;
        }
    }
    .coaching-run__tabs {
        position: absolute;
        height: calc(100% - 48px);
    }
    .item-group-name {
        font-size: 18px;
        font-weight: 500;
    }
    .item-group-table {
        width: 100%;
        font-size: 12px;
    }
    .item-group-table thead th {
        font-size: 11px;
        font-weight: 400;
        text-align: left;
    }
    .item-group-table tbody tr td {
        height: 32px;
    }
    .item-group-table tbody tr:nth-child(even) {
        background-color: #e8ebe8;
    }
    .item-gewichtung-row {
        width: 140px;
    }
    .item-gewichtung-diff {
        width: 60px;
        padding-right: 6px;
    }
    .item-group-table .item-gewichtung {
        margin: 0 4px;
    }
    .item-bewertung {
        width: 60px;
        max-width: 80px;
    }
    .item-bewertung-red {
        width: 16px;
        height: 16px;
        border: 2px solid #d91111;
        border-radius: 50%;
    }
    .item-bewertung-yellow {
        width: 16px;
        height: 16px;
        border: 4px solid #F5BC00;
        border-radius: 50%;
    }
    .item-bewertung-green {
        width: 16px;
        height: 16px;
        background-color: #49BA4E;
        border-radius: 50%;
    }
    .item-group-table-summary {
        width: 100%;
        margin-bottom: 32px;
        font-size: 13px;
        font-weight: 500;
    }
    .sum-group-table-footer {
        font-size: 13px;
        font-weight: 500;
        background-color: rgba(220, 220, 232, 0.4);
        border-top: 1px solid rgba(10, 0, 10, 0.87);
    }
    .item-group-table-footer {
        width: 100%;
        margin-top: 8px;
        margin-bottom: 0;
        font-size: 13px;
        font-weight: 500;
        color: white;
        background-color: #1c3257;
    }
    .item-group-table-footer-weighted {
        width: 100%;
        margin-bottom: 0;
        font-size: 13px;
        font-weight: 500;
        color: white;
        background-color: #e3ecf3;
    }
    .digital-clock-wrapper {
        display: flex;
        align-items: center;
        width: 260px;
    }
    .coaching-summary-group .item-label {
        font-weight: 700;
    }
</style>
